"use client"

import * as React from "react";
import { User } from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "@/core/components/avatar";
import { cn } from "@/lib/utils";

/**
 * Palette the server uses to assign `avatar_color` at signup. Keep in sync —
 * when a payload lacks `avatar_color`, we hash the display name into the same
 * palette so the client-derived color matches what the server would assign.
 */
const AVATAR_PALETTE = [
  "#F5A623",
  "#00A896",
  "#FF6F61",
  "#FFC107",
  "#34A853",
  "#FDCB9E",
  "#F44336",
  "#1976D2",
  "#FFEB3B",
  "#00BCD4",
];

/** First letter of the first word + first letter of the last word, uppercased. */
export function getInitials(name?: string | null): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/);
  const first = words[0][0] ?? "";
  const last = words.length > 1 ? words[words.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
}

function colorFromName(name: string): string {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
}

type UserAvatarProps = {
  /** Display name or "First Last" — used for initials and the derived color. */
  name?: string | null;
  /** Avatar image URL; the fallback shows when empty or the image fails. */
  src?: string | null;
  /** The user's `avatar_color`; falls back to a name-derived palette color. */
  color?: string | null;
  /** Passed to the Avatar root (sizing, e.g. "h-10 w-10"). */
  className?: string;
  /** Passed to the fallback (text size overrides, e.g. "text-3xl"). */
  fallbackClassName?: string;
} & Omit<React.ComponentPropsWithoutRef<typeof Avatar>, "color">;

/**
 * Canonical user avatar. Never shows a generic placeholder for a named user:
 * when there is no image, it renders the user's initials on their avatar
 * color (or a deterministic name-derived color when `avatar_color` is empty).
 */
const UserAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  UserAvatarProps
>(({ name, src, color, className, fallbackClassName, ...props }, ref) => {
  const initials = getInitials(name);
  const backgroundColor = color?.trim() || (initials ? colorFromName((name ?? "").trim()) : undefined);

  return (
    <Avatar ref={ref} className={className} {...props}>
      {src ? <AvatarImage src={src} alt={name ?? ""} /> : null}
      {initials ? (
        <AvatarFallback
          className={cn("font-bold text-white", fallbackClassName)}
          style={{ backgroundColor }}
        >
          {initials}
        </AvatarFallback>
      ) : (
        <AvatarFallback className={cn("text-muted-foreground", fallbackClassName)}>
          <User className="h-1/2 w-1/2" aria-hidden="true" />
        </AvatarFallback>
      )}
    </Avatar>
  );
});
UserAvatar.displayName = "UserAvatar";

export { UserAvatar };
